import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import MenuGuideButton from '../components/MenuGuideButton'
import { EmptyState, ErrorState } from '../components/ui/feedback-state'
import { api, getApiErrorMessage } from '../lib/api'
import { formatApiDateTimeDisplay } from '../lib/date'
import { PageFrame, PageHeader } from '../ui/PageLayout'

function unwrap(res) {
  const body = res?.data
  if (body && Object.prototype.hasOwnProperty.call(body, 'data')) return body.data
  return body
}

function normalizeSearchPayload(raw) {
  if (Array.isArray(raw)) {
    return { items: raw, page: 0, size: raw.length, hasNext: false, totalReturned: raw.length }
  }
  const payload = raw && typeof raw === 'object' ? raw : {}
  const items = Array.isArray(payload.items) ? payload.items : []
  return {
    items,
    page: Number.isFinite(Number(payload.page)) ? Number(payload.page) : 0,
    size: Number.isFinite(Number(payload.size)) ? Number(payload.size) : items.length,
    hasNext: payload.hasNext === true,
    totalReturned: Number.isFinite(Number(payload.totalReturned)) ? Number(payload.totalReturned) : items.length,
  }
}

function tagsText(tags) {
  return Array.isArray(tags) ? tags.filter(Boolean).join(', ') : ''
}

function resultTitle(row) {
  return row?.title || row?.structuredSummary?.headline || '새 메모'
}

const MATCH_FIELD_LABELS = {
  title: '제목',
  content: '내용',
  category: '분류',
  tag: '태그',
  customerName: '고객명',
  customerPhone: '연락처',
}

function HighlightedText({ text, query, className = '' }) {
  const value = String(text || '')
  const needle = String(query || '').trim()
  if (!needle) return <span className={className}>{value}</span>
  const lower = value.toLowerCase()
  const q = needle.toLowerCase()
  const parts = []
  let cursor = 0
  let idx = lower.indexOf(q)
  while (idx >= 0) {
    if (idx > cursor) parts.push({ text: value.slice(cursor, idx), hit: false })
    parts.push({ text: value.slice(idx, idx + needle.length), hit: true })
    cursor = idx + needle.length
    idx = lower.indexOf(q, cursor)
  }
  if (cursor < value.length) parts.push({ text: value.slice(cursor), hit: false })
  return (
    <span className={className}>
      {parts.map((part, index) => (part.hit
        ? <mark key={`${part.text}:${index}`} className="rounded bg-amber-100 px-0.5 text-amber-900">{part.text}</mark>
        : <React.Fragment key={`${part.text}:${index}`}>{part.text}</React.Fragment>))}
    </span>
  )
}

export default function CustomerMemoSearchPage() {
  const [query, setQuery] = useState('')
  const [activeQuery, setActiveQuery] = useState('')
  const [tag, setTag] = useState('')
  const [category, setCategory] = useState('')
  const [sort, setSort] = useState('updated_desc')
  const [pinnedOnly, setPinnedOnly] = useState(false)
  const [hasAttachments, setHasAttachments] = useState(false)
  const [hasFollowUp, setHasFollowUp] = useState(false)
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(0)
  const [hasNext, setHasNext] = useState(false)
  const [totalReturned, setTotalReturned] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const tagOptions = useMemo(() => {
    const values = new Set()
    rows.forEach((row) => (Array.isArray(row?.tags) ? row.tags : []).forEach((item) => {
      const value = String(item || '').trim()
      if (value) values.add(value)
    }))
    return Array.from(values).slice(0, 30)
  }, [rows])

  const categoryOptions = useMemo(() => {
    const values = new Set()
    rows.forEach((row) => {
      const value = String(row?.category || '').trim()
      if (value) values.add(value)
    })
    return Array.from(values).slice(0, 30)
  }, [rows])

  const load = async ({ nextPage = 0, append = false } = {}) => {
    setLoading(true)
    setError('')
    try {
      const q = query.trim()
      const res = await api.get('/customer-management/customer-memos/search', {
        params: {
          ...(q ? { q } : {}),
          ...(tag.trim() ? { tag: tag.trim() } : {}),
          ...(category.trim() ? { category: category.trim() } : {}),
          ...(pinnedOnly ? { pinned: true } : {}),
          ...(hasAttachments ? { hasAttachments: true } : {}),
          ...(hasFollowUp ? { hasFollowUp: true } : {}),
          page: nextPage,
          size: 20,
          sort,
        },
        __silent: true,
      })
      const payload = normalizeSearchPayload(unwrap(res))
      setRows((prev) => (append ? [...prev, ...payload.items] : payload.items))
      setPage(payload.page)
      setHasNext(payload.hasNext)
      setTotalReturned(payload.totalReturned)
      setActiveQuery(q)
    } catch (e) {
      setError(getApiErrorMessage(e, '고객메모를 검색하지 못했습니다.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load({ nextPage: 0 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <PageFrame>
      <PageHeader
        title="고객메모"
        description="고객별 상담 메모를 한 화면에서 찾고 후속 업무로 이어갑니다."
        titleAccessory={<MenuGuideButton slug="support-customers" title="고객관리 가이드" />}
        actions={(
          <Link className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" to="/support/customers">
            고객목록
          </Link>
        )}
      />
      <div className="mx-auto w-full max-w-7xl">

      <section className="mb-4 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_10rem_10rem_auto]">
          <input
            type="search"
            data-testid="customer-memo-global-search-input"
            className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load({ nextPage: 0 }) }}
            placeholder="고객명, 연락처, 제목, 내용, 태그 검색"
          />
          <input
            className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load({ nextPage: 0 }) }}
            list="customer-memo-search-categories"
            placeholder="분류"
          />
          <input
            className="min-w-0 rounded border border-slate-300 px-3 py-2 text-sm"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load({ nextPage: 0 }) }}
            list="customer-memo-search-tags"
            placeholder="태그"
          />
          <button
            type="button"
            className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            disabled={loading}
            data-testid="customer-memo-global-search-submit"
            onClick={() => load({ nextPage: 0 })}
          >
            {loading ? '검색 중' : '검색'}
          </button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <select
            className="rounded border border-slate-300 bg-white px-2 py-1.5 font-medium"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            disabled={loading}
          >
            <option value="updated_desc">최근 수정순</option>
            <option value="created_desc">최근 작성순</option>
            <option value="pinned_first">고정 우선</option>
            <option value="customer_asc">고객명순</option>
            <option value="title_asc">제목순</option>
          </select>
          <label className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 font-medium">
            <input type="checkbox" className="h-3.5 w-3.5" checked={pinnedOnly} onChange={(e) => setPinnedOnly(e.target.checked)} disabled={loading} />
            고정만
          </label>
          <label className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 font-medium">
            <input type="checkbox" className="h-3.5 w-3.5" checked={hasAttachments} onChange={(e) => setHasAttachments(e.target.checked)} disabled={loading} />
            첨부 있음
          </label>
          <label className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 font-medium">
            <input type="checkbox" className="h-3.5 w-3.5" checked={hasFollowUp} onChange={(e) => setHasFollowUp(e.target.checked)} disabled={loading} />
            후속조치
          </label>
        </div>
        <datalist id="customer-memo-search-categories">
          {categoryOptions.map((item) => <option key={item} value={item} />)}
        </datalist>
        <datalist id="customer-memo-search-tags">
          {tagOptions.map((item) => <option key={item} value={item} />)}
        </datalist>
      </section>

      {error ? <ErrorState description={error} onAction={() => load({ nextPage: 0 })} className="mb-4 rounded-xl border border-rose-100 bg-white py-6" /> : null}

      <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 text-sm">
          <div className="font-bold text-slate-900">검색 결과</div>
          <div className="text-xs text-slate-500">{totalReturned.toLocaleString('ko-KR')}건</div>
        </div>
        {loading && rows.length === 0 ? (
          <p className="px-4 py-8 text-sm text-slate-500">불러오는 중</p>
        ) : rows.length === 0 ? (
          <EmptyState title="검색된 고객메모가 없습니다." description="검색어, 분류, 태그 조건을 바꿔 확인해 주세요." className="py-10" />
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((row) => {
              const fields = Array.isArray(row.matchedFields) ? row.matchedFields : []
              const snippet = row.searchSnippet || row.summary || row.plainText || '내용 없음'
              return (
                <article key={row.id} data-testid="customer-memo-global-result" className="p-4 hover:bg-slate-50/70">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        {row.pinned ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">고정</span> : null}
                        {row.category ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{row.category}</span> : null}
                        <h2 className="min-w-0 truncate text-sm font-black text-slate-900"><HighlightedText text={resultTitle(row)} query={activeQuery} /></h2>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        <HighlightedText text={row.customerName || '고객'} query={activeQuery} /> {row.customerPhone ? <><span>· </span><HighlightedText text={row.customerPhone} query={activeQuery} /></> : null} · {formatApiDateTimeDisplay(row.updatedAtDisplay, row.updatedAt)}
                      </div>
                      {fields.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1 text-[11px] text-slate-500">
                          {fields.map((field) => <span key={`${row.id}:field:${field}`} className="rounded bg-blue-50 px-1.5 py-0.5 font-semibold text-blue-700">{MATCH_FIELD_LABELS[field] || field}</span>)}
                        </div>
                      ) : null}
                      <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-700"><HighlightedText text={snippet} query={activeQuery} /></p>
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-slate-500">
                        {tagsText(row.tags) ? tagsText(row.tags).split(', ').map((item) => <span key={`${row.id}:${item}`}>#{item}</span>) : null}
                        {row.attachmentCount ? <span>첨부 {row.attachmentCount}</span> : null}
                        {row.hasLinks ? <span>링크</span> : null}
                        {Array.isArray(row.actionSuggestions) && row.actionSuggestions.length > 0 ? <span>후속조치 추천</span> : null}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Link className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-brand-navy hover:bg-slate-50" to={`/support/customers/${row.customerRefId}#customer-memos`}>
                        메모 열기
                      </Link>
                    </div>
                  </div>
                </article>
              )
            })}
            {hasNext ? (
              <div className="flex justify-center px-4 py-4">
                <button
                  type="button"
                  className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  disabled={loading}
                  onClick={() => load({ nextPage: page + 1, append: true })}
                >
                  {loading ? '불러오는 중' : '더 보기'}
                </button>
              </div>
            ) : null}
          </div>
        )}
      </section>
      </div>
    </PageFrame>
  )
}